import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Heart, Shield, FileText, Download } from "lucide-react";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageMeta } from "@/components/site/PageMeta";
import {
  PREMIUM_TIERS,
  ALA_CARTE,
  COMPASSION_PLAN,
  SLIDING_SCALE_TIERS,
  BOUNDARIES,
  POSITIONING,
  type PremiumTier,
} from "@/lib/fsap-data";
import { trackCTA } from "@/lib/tracking";
import { TravelEstimator } from "@/components/advocacy/TravelEstimator";
import { RetainerPdfButton } from "@/components/advocacy/RetainerPdfButton";
import { TierDeliverables } from "@/components/advocacy/TierDeliverables";
import { StrategySessionModal } from "@/components/advocacy/StrategySessionModal";
import { FAQSection } from "@/components/site/FAQSection";
import { buildFAQPageJsonLd, type FAQItem } from "@/content/faq";

/**
 * Retainer-specific FAQ. Rendered by <FAQSection> and marked up as FAQPage
 * JSON-LD from the SAME array so schema and visible copy never drift.
 */
const RETAINER_FAQ: FAQItem[] = [
  {
    q: "How fast will I get a response on each retainer tier?",
    a: "Foundation clients receive email responses within two business days during standard hours. Priority clients receive same-business-day responses and expedited scheduling for meetings and document review. Elite clients add after-hours access for urgent, case-relevant events such as an imminent ARD, court appearance, or medical escalation. After-hours access is not a 24/7 crisis line — genuine emergencies still require 911 or the Texas 988 crisis line.",
  },
  {
    q: "How is travel billed, and what counts as travel time?",
    a: "Travel inside the San Antonio metro is included in the retainer for scheduled meetings. Beyond the metro, travel is quoted in writing before the trip and confirmed in an engagement addendum: mileage at the current IRS business rate, portal-to-portal travel time, lodging at actual cost when an overnight is required, and a flat per diem for meals. You approve the estimate before anything is booked, and the invoice never exceeds the approved estimate without a new written approval.",
  },
  {
    q: "How is my information kept confidential?",
    a: "Everything shared inside the engagement is treated as confidential and is stored in access-controlled systems. Records are only released to schools, districts, attorneys, agencies, or medical providers with your written, purpose-specific authorization, which you can revoke at any time. Advocacy is not a licensed therapeutic relationship, so it does not carry clinical privilege, and mandatory reporting duties for suspected abuse or neglect and imminent-harm situations still apply. Anonymized composite examples are only used publicly with signed release.",
  },
  {
    q: "How do change requests and scope changes work mid-engagement?",
    a: "Send change requests in writing by email. Routine adjustments — swapping a scheduled meeting, reprioritizing a document, adding a contact — are absorbed inside the retainer. Changes that expand scope, such as adding a new school, agency, court matter, or setting, receive a written scope amendment with revised hours and fees before any work begins. Mid-cycle upgrades take effect immediately when a case escalates; downgrades take effect at the next renewal so representation stays stable during active advocacy.",
  },
  {
    q: "What is the billing cycle, and can I pause or cancel?",
    a: "Retainers bill monthly in advance and renew until cancelled. Cancel with 30 days written notice; the current paid cycle is honored and unused prepaid hours in that cycle are not refunded but can be used through the end of the term. Short pauses are available when a case goes dormant, at which point your tier pricing is held for the agreed pause window.",
  },
  {
    q: "Are unused retainer hours rolled over?",
    a: "Retainer hours are scoped per month and do not roll over, because capacity is reserved for your case whether or not it is fully used. If your case consistently runs under scope, we downgrade the tier at renewal rather than banking hours — you should only pay for the coverage you actually need.",
  },
];

const AdvocacyPricing = () => {
  const [modalTier, setModalTier] = useState<PremiumTier | null>(null);
  return (
    <div className="min-h-screen bg-bg-primary text-fg-primary">
      <PageMeta
        title="FSAP Pricing — Retainers, À La Carte, Compassion Plan | Dr. Sully"
        description="Transparent Full-Scope Advocacy Partner pricing: monthly retainers, à la carte services, an SSI-level Compassion Plan, plus sliding scale and scholarships for documented hardship."
        path="/advocacy/pricing"
        jsonLd={{
          "@context": "https://schema.org",
          ...buildFAQPageJsonLd(
            RETAINER_FAQ,
            "https://drsullyspeaks.com/advocacy/pricing#faq",
          ),
        }}
      />
      <SiteNav />

      <main>
        {/* Header */}
        <section className="border-b border-fg-muted/10 bg-bg-secondary">
          <div className="max-w-6xl mx-auto px-6 py-20 md:py-28">
            <p className="font-display uppercase tracking-[0.24em] text-gold text-xs mb-4">FSAP Pricing</p>
            <h1 className="font-display text-4xl md:text-6xl leading-[1.05] max-w-4xl">
              Advocacy pricing built for real families — with room for those living on disability income.
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-fg-secondary italic">{POSITIONING}</p>
          </div>
        </section>

        {/* Premium retainers */}
        <section className="max-w-6xl mx-auto px-6 py-20">
          <div className="mb-10">
            <h2 className="font-display text-3xl md:text-4xl">Premium FSAP Retainers</h2>
            <p className="text-fg-secondary mt-3 max-w-2xl">
              Monthly retainers for ongoing advocacy. All tiers include written meeting prep,
              post-meeting recaps, and clear boundaries in writing.
            </p>
            <div className="mt-6">
              <RetainerPdfButton />
              <p className="text-xs text-fg-muted mt-2">
                Instant download · no email required · generated with today&rsquo;s published rates.
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {PREMIUM_TIERS.map((tier) => (
              <div
                key={tier.id}
                className="border border-fg-muted/15 bg-bg-secondary/40 p-8 flex flex-col"
              >
                <div className="font-display uppercase tracking-[0.18em] text-gold text-xs">
                  {tier.name}
                </div>
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="font-display text-4xl">${tier.monthly.toLocaleString()}</span>
                  <span className="text-fg-secondary text-sm">/month</span>
                </div>
                <p className="mt-3 text-sm text-fg-secondary">{tier.tagline}</p>
                <p className="mt-4 text-xs uppercase tracking-[0.14em] text-fg-muted">Best for</p>
                <p className="text-sm">{tier.bestFor}</p>
                <ul className="mt-5 space-y-2 text-sm flex-1">
                  {tier.features.map((f) => (
                    <li key={f} className="flex gap-2">
                      <Check className="h-4 w-4 text-gold shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => {
                    trackCTA("fsap_pricing_tier_click", { tier: tier.id, monthly: tier.monthly });
                    setModalTier(tier);
                  }}
                  className="mt-6 inline-flex items-center justify-center gap-2 bg-cta text-white font-display uppercase tracking-[0.18em] text-xs px-5 py-3 hover:brightness-110"
                >
                  Apply for {tier.name.split(" ")[0]}
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
                <Link
                  to={`/book?price=${tier.priceId}`}
                  onClick={() =>
                    trackCTA("fsap_pricing_tier_subscribe_click", {
                      tier: tier.id,
                      monthly: tier.monthly,
                      price_id: tier.priceId,
                    })
                  }
                  className="mt-3 inline-flex items-center justify-center gap-2 border border-gold/50 text-gold font-display uppercase tracking-[0.18em] text-xs px-5 py-3 hover:bg-gold/10"
                >
                  Start monthly plan
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
                <p className="mt-2 text-[11px] text-fg-muted text-center">
                  Secure Stripe checkout · sign-in required · cancel anytime
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Tier deliverables checklist */}
        <section className="border-t border-fg-muted/10 bg-bg-secondary">
          <div className="max-w-6xl mx-auto px-6 py-20">
            <TierDeliverables />
          </div>
        </section>

        {/* Travel estimator */}
        <section className="border-t border-fg-muted/10">
          <div className="max-w-6xl mx-auto px-6 py-20">
            <TravelEstimator />
          </div>
        </section>

        {/* À la carte */}
        <section className="border-t border-fg-muted/10 bg-bg-secondary">
          <div className="max-w-6xl mx-auto px-6 py-20">
            <h2 className="font-display text-3xl md:text-4xl">À La Carte Services</h2>
            <p className="text-fg-secondary mt-3 max-w-2xl">
              Single-engagement pricing when a retainer isn't the right structure yet.
            </p>
            <div className="mt-8 border border-fg-muted/15 divide-y divide-fg-muted/10">
              {ALA_CARTE.map((item) => (
                <div key={item.id} className="p-5 md:p-6 grid md:grid-cols-[1fr_auto] gap-3 md:items-center">
                  <div>
                    <div className="font-display text-lg">{item.name}</div>
                    {item.notes && <div className="text-xs text-fg-muted mt-1">{item.notes}</div>}
                  </div>
                  <div className="text-right">
                    <div className="font-display text-xl">{item.price}</div>
                    <div className="text-xs uppercase tracking-[0.14em] text-fg-muted">{item.unit}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Compassion Plan */}
        <section className="max-w-6xl mx-auto px-6 py-20">
          <div className="border border-gold/40 bg-gradient-to-br from-bg-secondary/50 to-transparent p-8 md:p-12">
            <div className="flex items-start gap-4 mb-6">
              <Heart className="h-8 w-8 text-gold shrink-0 mt-1" />
              <div>
                <p className="font-display uppercase tracking-[0.24em] text-gold text-xs mb-2">
                  Accessible FSAP
                </p>
                <h2 className="font-display text-3xl md:text-4xl">{COMPASSION_PLAN.name}</h2>
              </div>
            </div>
            <p className="text-fg-secondary max-w-3xl">{COMPASSION_PLAN.purpose}</p>
            <div className="mt-6 flex flex-wrap items-baseline gap-x-6 gap-y-2">
              <div>
                <span className="font-display text-5xl">${COMPASSION_PLAN.monthly}</span>
                <span className="text-fg-secondary text-sm">/month</span>
              </div>
              <div className="text-sm text-fg-muted">
                For clients on approximately <strong>{COMPASSION_PLAN.targetIncome}</strong>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-8 mt-8">
              <div>
                <h3 className="font-display uppercase tracking-[0.16em] text-xs text-gold mb-3">
                  What's included
                </h3>
                <ul className="space-y-2 text-sm">
                  {COMPASSION_PLAN.features.map((f) => (
                    <li key={f} className="flex gap-2">
                      <Check className="h-4 w-4 text-gold shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="font-display uppercase tracking-[0.16em] text-xs text-gold mb-3">
                  Boundaries
                </h3>
                <ul className="space-y-2 text-sm text-fg-secondary">
                  {COMPASSION_PLAN.boundaries.map((b) => (
                    <li key={b} className="flex gap-2">
                      <Shield className="h-4 w-4 text-fg-muted shrink-0 mt-0.5" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <Link
              to="/advocacy/intake?tier=compassion"
              onClick={() =>
                trackCTA("fsap_compassion_cta_click", { location: "pricing_page" })
              }
              className="mt-8 inline-flex items-center gap-2 bg-cta text-white font-display uppercase tracking-[0.18em] text-xs px-6 py-3.5 hover:brightness-110"
            >
              Apply for the Compassion Plan
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/advocacy/compassion-apply"
              onClick={() =>
                trackCTA("fsap_compassion_subscribe_click", {
                  location: "pricing_page",
                  price_id: COMPASSION_PLAN.priceId,
                  destination: "/advocacy/compassion-apply",
                })
              }
              className="mt-8 md:ml-4 inline-flex items-center gap-2 border border-gold/50 text-gold font-display uppercase tracking-[0.18em] text-xs px-6 py-3.5 hover:bg-gold/10"
            >
              Apply to enroll (${COMPASSION_PLAN.monthly}–$129/mo)
              <ArrowRight className="h-4 w-4" />
            </Link>
            <p className="mt-3 text-xs text-fg-muted">
              Approval required before billing. Requests are reviewed by hand (3–5 business days);
              approved households get a secure Stripe checkout link at their approved rate. Enrollment
              is reviewed quarterly and documentation of income may be requested.
            </p>
          </div>
        </section>

        {/* Sliding scale */}
        <section className="border-t border-fg-muted/10 bg-bg-secondary">
          <div className="max-w-6xl mx-auto px-6 py-20">
            <h2 className="font-display text-3xl md:text-4xl">Sliding Scale, Scholarships & Fee Waivers</h2>
            <p className="text-fg-secondary mt-3 max-w-3xl">
              Sliding scale options, scholarships, and fee waivers may be available based on income, household
              size, and documented hardship. These are case-by-case, tied to documented need and available
              funding, and may be time-limited or scoped to specific services or events.
            </p>
            <div className="grid md:grid-cols-3 gap-4 mt-8">
              {SLIDING_SCALE_TIERS.map((s) => (
                <div key={s.label} className="border border-fg-muted/15 p-6">
                  <div className="font-display text-2xl text-gold">{s.label}</div>
                  <div className="text-sm text-fg-secondary mt-2">{s.income}</div>
                </div>
              ))}
            </div>
            <div className="mt-8 p-6 border border-fg-muted/15 bg-bg-primary/40">
              <p className="text-sm">
                <strong>How to request:</strong> Ask your caseworker, social worker, or support coordinator
                to contact the FSAP Care Team, or submit a request directly through the intake form and
                describe your situation in the financial hardship section.
              </p>
              <Link
                to="/advocacy/intake?financial=review"
                onClick={() =>
                  trackCTA("fsap_sliding_scale_request", { location: "pricing_page" })
                }
                className="mt-4 inline-flex items-center gap-2 border border-gold/50 text-fg-primary font-display uppercase tracking-[0.18em] text-xs px-5 py-3 hover:bg-gold/10"
              >
                Request a financial review
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        </section>

        {/* Downloads */}
        <section className="max-w-6xl mx-auto px-6 py-20">
          <h2 className="font-display text-3xl md:text-4xl mb-6">Client Documents</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { name: "Sales Sheet (1 page)", file: "FSAP_Sales_Sheet.pdf", event: "fsap_sales_sheet_download" },
              { name: "Pricing Sheet (2 pages)", file: "FSAP_Pricing_Sheet.pdf", event: "fsap_pricing_sheet_download" },
              { name: "Service Agreement Draft", file: "FSAP_Service_Agreement_Draft.pdf", event: "fsap_agreement_download" },
            ].map((d) => (
              <a
                key={d.file}
                href={`/docs/${d.file}`}
                onClick={() => trackCTA(d.event, { file: d.file })}
                className="border border-fg-muted/15 p-6 flex items-center gap-4 hover:border-gold/40 hover:bg-bg-secondary/40 transition-colors"
              >
                <FileText className="h-8 w-8 text-gold shrink-0" />
                <div className="flex-1">
                  <div className="font-display text-sm">{d.name}</div>
                  <div className="text-xs text-fg-muted mt-1">PDF · click to download</div>
                </div>
                <Download className="h-4 w-4 text-fg-muted" />
              </a>
            ))}
          </div>
          <div className="mt-6">
            <RetainerPdfButton variant="outline" />
          </div>
          <p className="text-xs text-fg-muted mt-4">
            The service agreement is a working DRAFT for attorney review before signature.
          </p>
        </section>

        {/* Boundaries */}
        <section className="border-t border-fg-muted/10 bg-bg-secondary">
          <div className="max-w-6xl mx-auto px-6 py-16">
            <h2 className="font-display text-2xl md:text-3xl mb-6">Boundaries & Disclaimers</h2>
            <ul className="space-y-3 max-w-3xl">
              {BOUNDARIES.map((b) => (
                <li key={b} className="text-sm text-fg-secondary flex gap-3">
                  <Shield className="h-4 w-4 text-gold shrink-0 mt-0.5" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Retainer FAQ */}
        <FAQSection
          items={RETAINER_FAQ}
          slugPrefix="retainer-faq"
          eyebrow="Retainers · Frequently Asked"
          heading="Response times, travel, confidentiality, and changes."
          subheading="The terms families and organizations ask about most before signing an FSAP retainer — answered plainly, before the agreement."
        />
      </main>

      <StrategySessionModal
        open={!!modalTier}
        onOpenChange={(o) => !o && setModalTier(null)}
        tierLabel={modalTier?.name ?? ""}
        tierId={modalTier?.id}
        source="advocacy_pricing_cards"
      />
      <SiteFooter />
    </div>
  );
};

export default AdvocacyPricing;
